import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Divider,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Snackbar,
  Alert,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  useMediaQuery,
  useTheme,
  Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import CalculateIcon from '@mui/icons-material/Calculate';
import type { Quote, QuoteItem, Job, Paid, User } from '../../types';
import { apiRequest, api } from '../../utils/api';
import ExcelEditor from '../../components/ExcelEditor';
import Calculator from '../../components/Calculator';
import ExcelJS from 'exceljs';

interface QuoteProps {
  jobId: number;
  onBack?: () => void;
  getJobById?: (id: number) => Job | undefined;
  asTab?: boolean; // If true, hide header and back button
  isActive?: boolean; // If true, component is currently active/visible
}

interface QuoteProductType {
  id: number;
  productType: string;
  unit: string | null;
  sellingPrice: number | null;
}

export default function Quote({ jobId, onBack, getJobById, asTab = false, isActive = true }: QuoteProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [expandedQuotes, setExpandedQuotes] = useState<Set<number>>(new Set());
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [quoteItemDialogOpen, setQuoteItemDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState<Quote | null>(null);
  const [editingQuoteItem, setEditingQuoteItem] = useState<QuoteItem | null>(null);
  const [currentQuoteId, setCurrentQuoteId] = useState<number | null>(null);
  const [productTypes, setProductTypes] = useState<QuoteProductType[]>([]);
  const [loading, setLoading] = useState(false);
  const [jobLoading, setJobLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [quickbooksAlertOpen, setQuickbooksAlertOpen] = useState(false);
  const [quickbooksAlertMessage, setQuickbooksAlertMessage] = useState('');
  const [quoteStatus, setQuoteStatus] = useState<Job['quoteStatus']>('No Quote');
  const [paidRecord, setPaidRecord] = useState<Paid | null>(null);
  const [paidFormData, setPaidFormData] = useState({
    price: 0,
    deposit: 0,
    balance: 0,
    paid: false,
  });
  const [paidLoading, setPaidLoading] = useState(false);
  const [quoteFormData, setQuoteFormData] = useState({
    name: '',
    status: 'Draft',
  });
  const [quoteItemFormData, setQuoteItemFormData] = useState<{
    description: string;
    widthInches: number;
    heightInches: number;
    sqFtRate: number;
    lnFtRate: number;
    hardware: number;
    otherChargesDescription: string;
    otherCharges: number;
    quantity: number;
    productTypeId: number | null;
    hardwareProductTypeId: number | null;
    discount: number;
  }>({
    description: '',
    widthInches: 0,
    heightInches: 0,
    sqFtRate: 0,
    lnFtRate: 0,
    hardware: 0,
    otherChargesDescription: '',
    otherCharges: 0,
    quantity: 1,
    productTypeId: null,
    hardwareProductTypeId: null,
    discount: 0,
  });

  // Create product type modal state
  const [createProductTypeDialogOpen, setCreateProductTypeDialogOpen] = useState(false);
  const [createProductTypeForm, setCreateProductTypeForm] = useState({
    productType: '',
    description: '',
    sellingPrice: '',
    unit: 'Sq ft',
  });
  const [createProductTypeSaving, setCreateProductTypeSaving] = useState(false);

  // Create hardware type modal state
  const [createHardwareTypeDialogOpen, setCreateHardwareTypeDialogOpen] = useState(false);
  const [createHardwareTypeForm, setCreateHardwareTypeForm] = useState({
    productType: '',
    description: '',
    sellingPrice: '',
    unit: 'Hardware',
  });
  const [createHardwareTypeSaving, setCreateHardwareTypeSaving] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [excelEditorOpen, setExcelEditorOpen] = useState(false);
  const [currentQuoteIdForExcel, setCurrentQuoteIdForExcel] = useState<number | null>(null);
  const [excelWorkbook, setExcelWorkbook] = useState<ExcelJS.Workbook | null>(null);
  // Track deposit for each quote (keyed by quote ID)
  const [quoteDeposits, setQuoteDeposits] = useState<Record<number, number>>({});
  // Track paid records for each quote (keyed by quote ID)
  const [quotePaidRecords, setQuotePaidRecords] = useState<Record<number, Paid | null>>({});
  // Track paid form data for each quote (keyed by quote ID)
  const [quotePaidFormData, setQuotePaidFormData] = useState<Record<number, {
    price: number;
    deposit: number;
    balance: number;
    paid: boolean;
  }>>({});
  // Track loading state for each quote's paid save
  const [quotePaidLoading, setQuotePaidLoading] = useState<Record<number, boolean>>({});
  // Alert modal state for job status update
  const [jobStatusAlertOpen, setJobStatusAlertOpen] = useState(false);
  const [newJobStatus, setNewJobStatus] = useState<string | null>(null);
  const [jobActiveUpdated, setJobActiveUpdated] = useState(false);
  // Calculator dialog state
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [calculatorField, setCalculatorField] = useState<'width' | 'height'>('width');

  // WhatsApp confirm modal state
  const [whatsappConfirmOpen, setWhatsappConfirmOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{ quoteId: number; quote: Quote } | null>(null);

  // Check if user is a Field Worker
  const isFieldWorker = currentUser?.role === 'FIELD AND SHOP TEAM';
  const isAdmin = currentUser?.role === 'SYSTEM ADMIN';

  // Load current user profile
  const loadCurrentUser = async () => {
    try {
      const response = await apiRequest('/users/profile/me', { method: 'GET' });
      setCurrentUser(response.data as User);
    } catch (error) {
      console.error('Failed to load current user:', error);
    }
  };

  // Calculation functions
  const calculateSquareFoot = (widthInches: number, heightInches: number): number => {
    return (widthInches * heightInches) / 144; // Convert square inches to square feet
  };

  const calculateLinearFoot = (widthInches: number): number => {
    return widthInches / 12;
  };

  const calculateRate = (
    squareFoot: number,
    sqFtRate: number,
    linearFoot: number,
    lnFtRate: number,
    hardware: number,
    otherCharges: number = 0
  ): number => {
    return squareFoot * sqFtRate + linearFoot * lnFtRate + hardware + otherCharges;
  };

  const calculateTotalAmountWithDiscounts = (quotes: Quote[]): number => {
    return quotes.reduce((sum, quote) => {
      return sum + (quote.totalAmount || 0);
    }, 0);
  };

  const loadJob = async () => {
    setJobLoading(true);
    try {
      const response = await apiRequest(`/jobs/${jobId}`, { method: 'GET' });
      const j = response.data as any;
      
      // Transform API response to match Job type
      const mappedJob: Job = {
        id: j.id,
        job: j.job_number,
        customer: j.customer_name || '',
        customerAddress: j.customer_address || '',
        customerPhone: j.customer_phone || '',
        jobAddress: j.job_address || '',
        apartment: j.apartment || '',
        status: (j.status || 'Estimate') as Job['status'],
        quoteStatus: j.quote_status as Job['quoteStatus'],
        payStatus: j.pay_status as Job['payStatus'],
        appointmentDate: j.appointment_date
          ? new Date(j.appointment_date).toLocaleDateString('en-US')
          : '',
        startTime: j.start_time || '',
        endTime: j.end_time || '',
        dueDate: j.due_date
          ? new Date(j.due_date).toLocaleDateString('en-US')
          : '',
        userId: j.user_id ?? undefined,
      };
      
      setJob(mappedJob);
      setQuoteStatus(mappedJob.quoteStatus || 'No Quote');
    } catch (error) {
      console.error('Error loading job:', error);
      setJob(null);
    } finally {
      setJobLoading(false);
    }
  };

  useEffect(() => {
    loadCurrentUser();
  }, []);

  useEffect(() => {
    // When used as a tab, load job directly from API
    // Otherwise, try to get it from getJobById
    if (asTab) {
      // Load job when tab becomes active to ensure fresh data
      if (isActive) {
        loadJob();
      }
    } else if (getJobById) {
      const foundJob = getJobById(jobId);
      setJob(foundJob || null);
      if (foundJob) {
        setQuoteStatus(foundJob.quoteStatus || 'No Quote');
      }
    }

    // Reload quotes and product types when component becomes active (when tab is clicked)
    if (isActive) {
      loadQuotes();
      loadProductTypes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, getJobById, isActive, asTab]);

  // Load paid record when quote status is Accept
  useEffect(() => {
    if (quoteStatus === 'Accept') {
      loadPaidRecord();
    }
  }, [quoteStatus, jobId]);

  // Update price field when quotes change and quote status is Accept
  // Only set price to totalAmount if price is 0 (initial state) and no paid record exists
  useEffect(() => {
    if (quoteStatus === 'Accept' && quotes.length > 0 && !paidRecord) {
      const totalAmount = calculateTotalAmountWithDiscounts(quotes);
      const isPaid = job?.payStatus === 'Paid';
      setPaidFormData((prev) => {
        // Only update price if it's 0 (initial state), otherwise preserve user-entered value
        const shouldUpdatePrice = prev.price === 0;
        return {
          ...prev,
          price: shouldUpdatePrice ? totalAmount : prev.price,
          balance: (shouldUpdatePrice ? totalAmount : prev.price) - prev.deposit,
          paid: isPaid,
        };
      });
    }
  }, [quotes, quoteStatus, paidRecord, job]);

  const loadQuotePaidRecord = async (quoteId: number) => {
    try {
      const response = await apiRequest(`/jobs/${jobId}/quotes/${quoteId}/paid`, { method: 'GET' });
      const data = response.data as any;
      
      if (!data) {
        setQuotePaidRecords((prev) => ({ ...prev, [quoteId]: null }));
        const quote = quotes.find(q => q.id === quoteId);
        setQuotePaidFormData((prev) => ({
          ...prev,
          [quoteId]: {
            price: quote?.totalAmount || 0,
            deposit: 0,
            balance: quote?.totalAmount || 0,
            paid: false,
          },
        }));
        return;
      }
      
      const paidRecord: Paid = {
        id: data.id,
        price: Number(data.price),
        deposit: Number(data.deposit),
        balance: Number(data.balance),
        paid: data.paid || false,
        quoteId: data.quote_id,
        jobId: data.job_id,
        userId: data.user_id,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
      
      setQuotePaidRecords((prev) => ({ ...prev, [quoteId]: paidRecord }));
      setQuotePaidFormData((prev) => ({
        ...prev,
        [quoteId]: {
          price: paidRecord.price,
          deposit: paidRecord.deposit,
          balance: paidRecord.balance,
          paid: paidRecord.paid || false,
        },
      }));
      setQuoteDeposits((prev) => ({ ...prev, [quoteId]: paidRecord.deposit }));
    } catch (error: any) {
      if (error.response?.status === 404) {
        setQuotePaidRecords((prev) => ({ ...prev, [quoteId]: null }));
        const quote = quotes.find(q => q.id === quoteId);
        setQuotePaidFormData((prev) => ({
          ...prev,
          [quoteId]: {
            price: quote?.totalAmount || 0,
            deposit: 0,
            balance: quote?.totalAmount || 0,
            paid: false,
          },
        }));
      } else {
        console.error('Error loading paid record for quote:', error);
      }
    }
  };

  const loadQuotes = async () => {
    setLoading(true);
    try {
      const response = await apiRequest(`/jobs/${jobId}/quotes`, { method: 'GET' });
      const data = (response.data || []) as any[];
      const loadedQuotes = data.map((q) => ({
        id: q.id,
        jobId: q.job_id,
        name: q.name || '',
        status: q.status || 'Draft',
        pdfUrl: q.pdf_url || null,
        totalAmount: q.total_amount != null ? Number(q.total_amount) : 0,
        createdAt: q.created_at || new Date().toISOString(),
        updatedAt: q.updated_at || new Date().toISOString(),
        items: (q.items || []).map((item: any) => ({
          id: item.id,
          quoteId: item.quote_id,
          description: item.description || '',
          widthInches: item.width_inches != null ? Number(item.width_inches) : 0,
          heightInches: item.height_inches != null ? Number(item.height_inches) : 0,
          sqFtRate: item.sq_ft_rate != null ? Number(item.sq_ft_rate) : 0,
          lnFtRate: item.ln_ft_rate != null ? Number(item.ln_ft_rate) : 0,
          hardware: item.hardware != null ? Number(item.hardware) : 0,
          otherChargesDescription: item.other_charges_description || '',
          otherCharges: item.other_charges != null ? Number(item.other_charges) : 0,
          quantity: item.quantity != null ? Number(item.quantity) : 1,
          productId: item.product_id != null ? Number(item.product_id) : null,
          hardwareId: item.hardware_id != null ? Number(item.hardware_id) : null,
          discount: item.discount != null ? Number(item.discount) : 0,
          lineTotal: item.line_total != null ? Number(item.line_total) : 0,
          createdAt: item.created_at || new Date().toISOString(),
          updatedAt: item.updated_at || new Date().toISOString(),
        })),
      }));
      
      setQuotes(loadedQuotes);
      
      // Load paid records for all approved quotes
      for (const quote of loadedQuotes) {
        if (quote.status === 'Approved') {
          await loadQuotePaidRecord(quote.id);
        }
      }
    } catch (error) {
      console.error('Error loading quotes:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load quotes',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadProductTypes = async () => {
    try {
      const response = await apiRequest('/producttype', { method: 'GET' });
      const data = (response.data || []) as any[];
      setProductTypes(
        data.map((p) => ({
          id: p.id,
          productType: p.productType,
          unit: p.unit,
          sellingPrice:
            p.sellingPrice != null && !Number.isNaN(Number(p.sellingPrice))
              ? Number(p.sellingPrice)
              : null,
        }))
      );
    } catch (error) {
      console.error('Error loading product types:', error);
    }
  };

  const handleCreateProductType = async () => {
    if (!createProductTypeForm.productType || !createProductTypeForm.sellingPrice) {
      setSnackbar({
        open: true,
        message: 'Product Type and Selling Price are required',
        severity: 'error',
      });
      return;
    }

    const numericSellingPrice = Number(createProductTypeForm.sellingPrice);

    if (Number.isNaN(numericSellingPrice)) {
      setSnackbar({
        open: true,
        message: 'Selling Price must be a valid number',
        severity: 'error',
      });
      return;
    }

    setCreateProductTypeSaving(true);
    try {
      const response = await apiRequest('/producttype', {
        method: 'PUT',
        body: JSON.stringify({
          productType: createProductTypeForm.productType,
          description: createProductTypeForm.description || null,
          sellingPrice: numericSellingPrice,
          unit: createProductTypeForm.unit || null,
        }),
      });
      const data = response.data as { productType: QuoteProductType };
      
      // Add the new product type to the list
      setProductTypes((prev) => [
        {
          id: data.productType.id,
          productType: data.productType.productType,
          unit: data.productType.unit,
          sellingPrice: data.productType.sellingPrice,
        },
        ...prev,
      ]);

      // Auto-select the newly created product type
      setQuoteFormData((prev) => {
        const selected = {
          id: data.productType.id,
          productType: data.productType.productType,
          unit: data.productType.unit,
          sellingPrice: data.productType.sellingPrice,
        };

        let sqFtRate = 0;
        let lnFtRate = 0;

        if (selected.sellingPrice != null) {
          if (selected.unit === 'Sq ft') {
            sqFtRate = selected.sellingPrice;
          } else if (selected.unit === 'Ln ft') {
            lnFtRate = selected.sellingPrice;
          }
        }

        return {
          ...prev,
          productTypeId: selected.id,
          sqFtRate,
          lnFtRate,
        };
      });

      setSnackbar({
        open: true,
        message: 'Product type created successfully',
        severity: 'success',
      });
      setCreateProductTypeDialogOpen(false);
      setCreateProductTypeForm({
        productType: '',
        description: '',
        sellingPrice: '',
        unit: 'Sq ft',
      });
    } catch (error) {
      console.error('Error creating product type:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to create product type',
        severity: 'error',
      });
    } finally {
      setCreateProductTypeSaving(false);
    }
  };

  const handleCreateHardwareType = async () => {
    if (!createHardwareTypeForm.productType || !createHardwareTypeForm.sellingPrice) {
      setSnackbar({
        open: true,
        message: 'Product Type and Selling Price are required',
        severity: 'error',
      });
      return;
    }

    const numericSellingPrice = Number(createHardwareTypeForm.sellingPrice);

    if (Number.isNaN(numericSellingPrice)) {
      setSnackbar({
        open: true,
        message: 'Selling Price must be a valid number',
        severity: 'error',
      });
      return;
    }

    setCreateHardwareTypeSaving(true);
    try {
      const response = await apiRequest('/producttype', {
        method: 'PUT',
        body: JSON.stringify({
          productType: createHardwareTypeForm.productType,
          description: createHardwareTypeForm.description || null,
          sellingPrice: numericSellingPrice,
          unit: createHardwareTypeForm.unit || null,
        }),
      });
      const data = response.data as { productType: QuoteProductType };
      
      // Add the new product type to the list
      setProductTypes((prev) => [
        {
          id: data.productType.id,
          productType: data.productType.productType,
          unit: data.productType.unit,
          sellingPrice: data.productType.sellingPrice,
        },
        ...prev,
      ]);

      // Auto-select the newly created hardware type
      setQuoteFormData((prev) => {
        const selected = {
          id: data.productType.id,
          productType: data.productType.productType,
          unit: data.productType.unit,
          sellingPrice: data.productType.sellingPrice,
        };

        const hardwareValue =
          selected.sellingPrice != null ? selected.sellingPrice : 0;

        return {
          ...prev,
          hardwareProductTypeId: selected.id,
          hardware: hardwareValue,
        };
      });

      setSnackbar({
        open: true,
        message: 'Hardware type created successfully',
        severity: 'success',
      });
      setCreateHardwareTypeDialogOpen(false);
      setCreateHardwareTypeForm({
        productType: '',
        description: '',
        sellingPrice: '',
        unit: 'Hardware',
      });
    } catch (error) {
      console.error('Error creating hardware type:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to create hardware type',
        severity: 'error',
      });
    } finally {
      setCreateHardwareTypeSaving(false);
    }
  };

  const handleCreateQuote = () => {
    setEditingQuote(null);
    setQuoteFormData({
      name: '',
      status: 'Draft',
    });
    setQuoteDialogOpen(true);
  };

  const handleEditQuote = (quote: Quote) => {
    setEditingQuote(quote);
    setQuoteFormData({
      name: quote.name,
      status: quote.status || 'Draft',
    });
    setQuoteDialogOpen(true);
  };

  const handleCreateQuoteItem = (quoteId: number) => {
    setCurrentQuoteId(quoteId);
    setEditingQuoteItem(null);
    setQuoteItemFormData({
      description: '',
      widthInches: 0,
      heightInches: 0,
      sqFtRate: 0,
      lnFtRate: 0,
      hardware: 0,
      otherChargesDescription: '',
      otherCharges: 0,
      quantity: 1,
      productTypeId: null,
      hardwareProductTypeId: null,
      discount: 0,
    });
    setQuoteItemDialogOpen(true);
  };

  const handleEditQuoteItem = (item: QuoteItem) => {
    setEditingQuoteItem(item);
    setCurrentQuoteId(item.quoteId);
    setQuoteItemFormData({
      description: item.description || '',
      widthInches: item.widthInches,
      heightInches: item.heightInches,
      sqFtRate: item.sqFtRate,
      lnFtRate: item.lnFtRate,
      hardware: item.hardware,
      otherChargesDescription: item.otherChargesDescription || '',
      otherCharges: item.otherCharges || 0,
      quantity: item.quantity,
      productTypeId: item.productId || null,
      hardwareProductTypeId: item.hardwareId || null,
      discount: item.discount || 0,
    });
    setQuoteItemDialogOpen(true);
  };

  const handleDeleteQuoteItem = async (quoteId: number, itemId: number) => {
    if (window.confirm('Are you sure you want to delete this quote item?')) {
      try {
        await apiRequest(`/jobs/${jobId}/quotes/${quoteId}/items/${itemId}`, { method: 'DELETE' });
        setSnackbar({
          open: true,
          message: 'Quote item deleted successfully',
          severity: 'success',
        });
        loadQuotes();
      } catch (error) {
        console.error('Error deleting quote item:', error);
        setSnackbar({
          open: true,
          message: 'Failed to delete quote item',
          severity: 'error',
        });
      }
    }
  };

  const handleDeleteQuote = async (quoteId: number) => {
    if (window.confirm('Are you sure you want to delete this quote?')) {
      try {
        await apiRequest(`/jobs/${jobId}/quotes/${quoteId}`, { method: 'DELETE' });
        setSnackbar({
          open: true,
          message: 'Quote deleted successfully',
          severity: 'success',
        });
        loadQuotes();
      } catch (error) {
        console.error('Error deleting quote:', error);
        setSnackbar({
          open: true,
          message: 'Failed to delete quote',
          severity: 'error',
        });
      }
    }
  };

  const handleQuoteStatusChange = async (quoteId: number, newStatus: string, previousStatus?: string) => {
    try {
      const response = await apiRequest(`/jobs/${jobId}/quotes/${quoteId}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: newStatus,
        }),
      });
      const data = response.data as any;
      
      setSnackbar({
        open: true,
        message: 'Quote status updated successfully',
        severity: 'success',
      });
      
      // Check if job status or active status was updated
      if (data.job_status_updated === true && data.new_job_status) {
        setNewJobStatus(data.new_job_status);
        setJobActiveUpdated(data.job_active_updated === true);
        setJobStatusAlertOpen(true);
        // Reload job data to reflect the updated status
        loadJob();
      } else if (data.job_active_updated === true && !data.job_status_updated) {
        // Only active status was updated (all quotes rejected)
        setNewJobStatus(null);
        setJobActiveUpdated(true);
        setJobStatusAlertOpen(true);
        // Reload job data to reflect the updated active status
        loadJob();
      }
      
      loadQuotes();
    } catch (error: any) {
      console.error('Error updating quote status:', error);

      // Revert the quote status in local state to prevent visual change
      if (previousStatus !== undefined) {
        setQuotes(prev => prev.map(q => q.id === quoteId ? { ...q, status: previousStatus } : q));
      }

      const errorData = error.response?.data;
      const errorCode = errorData?.error;
      const statusCode = error.response?.status;

      // Check for QuickBooks not connected error
      if (errorCode === 'QUICKBOOKS_NOT_CONNECTED' ||
          (statusCode === 400 && errorData?.message?.includes('QuickBooks'))) {
        const message = errorData?.message || 'QuickBooks Online is not connected. Please connect QuickBooks before approving quotes.';
        setQuickbooksAlertMessage(message);
        setQuickbooksAlertOpen(true);
        setSnackbar({ open: true, message, severity: 'error' });
      } else {
        setSnackbar({
          open: true,
          message: errorData?.message || 'Failed to update quote status',
          severity: 'error',
        });
      }
    }
  };

  const loadPaidRecord = async () => {
    try {
      const response = await apiRequest(`/jobs/${jobId}/paid`, { method: 'GET' });
      const data = response.data as any;
      
      // Handle null response (no paid record exists)
      if (!data) {
        setPaidRecord(null);
        // Only set price to totalAmount if price is 0 (initial state)
        // Otherwise preserve user-entered value
        const totalAmount = calculateTotalAmountWithDiscounts(quotes);
        // Get pay_status from job
        const isPaid = job?.payStatus === 'Paid';
        setPaidFormData((prev) => {
          const shouldUpdatePrice = prev.price === 0;
          const newPrice = shouldUpdatePrice ? totalAmount : prev.price;
          return {
            price: newPrice,
            deposit: prev.deposit,
            balance: newPrice - prev.deposit,
            paid: isPaid,
          };
        });
        return;
      }
      
      setPaidRecord({
        id: data.id,
        price: Number(data.price),
        deposit: Number(data.deposit),
        balance: Number(data.balance),
        quoteId: data.quote_id,
        jobId: data.job_id,
        userId: data.user_id,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      });
      // Get pay_status from job
      const isPaid = job?.payStatus === 'Paid';
      setPaidFormData({
        price: Number(data.price),
        deposit: Number(data.deposit),
        balance: Number(data.balance),
        paid: isPaid,
      });
    } catch (error: any) {
      // Handle 404 or other errors gracefully
      if (error.response?.status === 404) {
        // No paid record exists yet
        setPaidRecord(null);
        // Only set price to totalAmount if price is 0 (initial state)
        // Otherwise preserve user-entered value
        const totalAmount = calculateTotalAmountWithDiscounts(quotes);
        // Get pay_status from job
        const isPaid = job?.payStatus === 'Paid';
        setPaidFormData((prev) => {
          const shouldUpdatePrice = prev.price === 0;
          const newPrice = shouldUpdatePrice ? totalAmount : prev.price;
          return {
            price: newPrice,
            deposit: prev.deposit,
            balance: newPrice - prev.deposit,
            paid: isPaid,
          };
        });
      } else {
        console.error('Error loading paid record:', error);
      }
    }
  };

  const handlePaidFormChange = (field: 'price' | 'deposit' | 'paid', value: number | boolean) => {
    if (field === 'paid') {
      setPaidFormData((prev) => ({
        ...prev,
        paid: value as boolean,
      }));
    } else if (field === 'price') {
      const price = value as number;
      const maxPrice = calculateTotalAmountWithDiscounts(quotes);
      const clampedPrice = Math.min(Math.max(0, price), maxPrice);
      setPaidFormData((prev) => ({
        ...prev,
        price: clampedPrice,
        balance: clampedPrice - prev.deposit,
      }));
    } else if (field === 'deposit') {
      const deposit = value as number;
      setPaidFormData((prev) => ({
        ...prev,
        deposit,
        balance: prev.price - deposit,
      }));
    }
  };

  const handleSavePaid = async () => {
    setPaidLoading(true);
    try {
      const response = await apiRequest(`/jobs/${jobId}/paid`, {
        method: 'POST',
        body: JSON.stringify({
          price: paidFormData.price,
          deposit: paidFormData.deposit,
          quote_id: quotes.length > 0 ? quotes[0].id : null,
          paid: paidFormData.paid,
        }),
      });
      const data = response.data as any;
      setPaidRecord({
        id: data.id,
        price: Number(data.price),
        deposit: Number(data.deposit),
        balance: Number(data.balance),
        quoteId: data.quote_id,
        jobId: data.job_id,
        userId: data.user_id,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      });

      const payStatus = paidFormData.paid ? 'Paid' : 'Unpaid';
      if (job) {
        setJob({ ...job, payStatus: payStatus as 'Paid' | 'Unpaid' });
      }

      setSnackbar({
        open: true,
        message: paidRecord ? 'Payment information updated successfully' : 'Payment information saved successfully',
        severity: 'success',
      });
    } catch (error: any) {
      console.error('Error saving paid record:', error);
      const errorData = error.response?.data;
      const errorCode = errorData?.error;
      const statusCode = error.response?.status;

      if (errorCode === 'QUICKBOOKS_NOT_CONNECTED' ||
          (statusCode === 400 && errorData?.message?.includes('QuickBooks'))) {
        const message = errorData?.message || 'QuickBooks Online is not connected. Please connect QuickBooks before updating payment information.';
        setQuickbooksAlertMessage(message);
        setQuickbooksAlertOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: errorData?.message || 'Failed to save payment information',
          severity: 'error',
        });
      }
    } finally {
      setPaidLoading(false);
    }
  };

  const handleSaveQuote = async () => {
    if (!quoteFormData.name) {
      setSnackbar({
        open: true,
        message: 'Quote name is required',
        severity: 'error',
      });
      return;
    }

    try {
      if (editingQuote) {
        // Update existing quote header
        await apiRequest(`/jobs/${jobId}/quotes/${editingQuote.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: quoteFormData.name,
            status: quoteFormData.status,
          }),
        });
        setSnackbar({
          open: true,
          message: 'Quote updated successfully',
          severity: 'success',
        });
      } else {
        // Create new quote header
        await apiRequest(`/jobs/${jobId}/quotes`, {
          method: 'POST',
          body: JSON.stringify({
            name: quoteFormData.name,
            status: quoteFormData.status,
          }),
        });
        setSnackbar({
          open: true,
          message: 'Quote created successfully',
          severity: 'success',
        });
      }
      setQuoteDialogOpen(false);
      setEditingQuote(null);
      loadQuotes();
      // Reload job data to update quote status after creating a new quote
      loadJob();
    } catch (error) {
      console.error('Error saving quote:', error);
      setSnackbar({
        open: true,
        message: editingQuote ? 'Failed to update quote' : 'Failed to create quote',
        severity: 'error',
      });
    }
  };

  const handleSaveQuoteItem = async () => {
    if (!currentQuoteId) {
      setSnackbar({
        open: true,
        message: 'Quote ID is missing',
        severity: 'error',
      });
      return;
    }

    if (!quoteItemFormData.description || quoteItemFormData.description.trim() === '') {
      setSnackbar({
        open: true,
        message: 'Description is required',
        severity: 'error',
      });
      return;
    }

    try {
      if (editingQuoteItem) {
        // Update existing quote item
        await apiRequest(`/jobs/${jobId}/quotes/${currentQuoteId}/items/${editingQuoteItem.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            description: quoteItemFormData.description,
            width_inches: quoteItemFormData.widthInches,
            height_inches: quoteItemFormData.heightInches,
            sq_ft_rate: quoteItemFormData.sqFtRate,
            ln_ft_rate: quoteItemFormData.lnFtRate,
            hardware: quoteItemFormData.hardware,
            other_charges_description: quoteItemFormData.otherChargesDescription,
            other_charges: quoteItemFormData.otherCharges,
            quantity: quoteItemFormData.quantity,
            product_id: quoteItemFormData.productTypeId,
            hardware_id: quoteItemFormData.hardwareProductTypeId,
            discount: quoteItemFormData.discount,
          }),
        });
        setSnackbar({
          open: true,
          message: 'Quote item updated successfully',
          severity: 'success',
        });
      } else {
        // Create new quote item
        await apiRequest(`/jobs/${jobId}/quotes/${currentQuoteId}/items`, {
          method: 'POST',
          body: JSON.stringify({
            description: quoteItemFormData.description,
            width_inches: quoteItemFormData.widthInches,
            height_inches: quoteItemFormData.heightInches,
            sq_ft_rate: quoteItemFormData.sqFtRate,
            ln_ft_rate: quoteItemFormData.lnFtRate,
            hardware: quoteItemFormData.hardware,
            other_charges_description: quoteItemFormData.otherChargesDescription,
            other_charges: quoteItemFormData.otherCharges,
            quantity: quoteItemFormData.quantity,
            product_id: quoteItemFormData.productTypeId,
            hardware_id: quoteItemFormData.hardwareProductTypeId,
            discount: quoteItemFormData.discount,
          }),
        });
        setSnackbar({
          open: true,
          message: 'Quote item created successfully',
          severity: 'success',
        });
      }
      setQuoteItemDialogOpen(false);
      setEditingQuoteItem(null);
      setCurrentQuoteId(null);
      loadQuotes();
    } catch (error: any) {
      console.error('Error saving quote item:', error);
      const apiMessage = error?.response?.data?.message;
      setSnackbar({
        open: true,
        message: apiMessage || (editingQuoteItem ? 'Failed to update quote item' : 'Failed to create quote item'),
        severity: 'error',
      });
    }
  };

  const handleDownloadTemplate = async (quoteId: number) => {
    setDownloadLoading(true);
    setCurrentQuoteIdForExcel(quoteId);
    
    try {
      // Load Excel template from backend
      const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';
      const url = `${API_BASE_URL}/jobs/${jobId}/quotes/${quoteId}/template-excel`;

      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to load template' }));
        throw new Error(errorData.message || 'Failed to load template');
      }

      const arrayBuffer = await response.arrayBuffer();
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(arrayBuffer);
      
      setExcelWorkbook(wb);
      setExcelEditorOpen(true);
      
      setSnackbar({
        open: true,
        message: 'Excel template loaded. Edit and click Download to generate PDF.',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error loading Excel template:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to load template',
        severity: 'error',
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleExcelEditorSave = async (workbook: ExcelJS.Workbook) => {
    if (!currentQuoteIdForExcel) return;
    
    setDownloadLoading(true);
    
    try {
      // Convert workbook to buffer
      const buffer = await workbook.xlsx.writeBuffer();
      
      // Create FormData to send Excel file
      const formData = new FormData();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      formData.append('excelFile', blob, `quote_${currentQuoteIdForExcel}_edited.xlsx`);
      
      // Send to backend to generate PDF
      const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';
      const url = `${API_BASE_URL}/jobs/${jobId}/quotes/${currentQuoteIdForExcel}/generate-pdf-from-excel`;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to generate PDF' }));
        throw new Error(errorData.message || 'Failed to generate PDF');
      }

      
      // Download the PDF
      const pdfBlob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `quote_${currentQuoteIdForExcel}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      // Reload quotes to get updated PDF URL
      await loadQuotes();
      
      setSnackbar({
        open: true,
        message: 'PDF generated and downloaded successfully. PDF has been saved to job files.',
        severity: 'success',
      });
      
      // Close the dialog
      setExcelEditorOpen(false);
      setExcelWorkbook(null);
      setCurrentQuoteIdForExcel(null);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to generate PDF',
        severity: 'error',
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleQuotePaidFormChange = (quoteId: number, field: 'price' | 'deposit' | 'paid', value: number | boolean) => {
    setQuotePaidFormData((prev) => {
      const current = prev[quoteId] || { price: 0, deposit: 0, balance: 0, paid: false };
      if (field === 'paid') {
        return {
          ...prev,
          [quoteId]: {
            ...current,
            paid: value as boolean,
          },
        };
      } else if (field === 'price') {
        const price = value as number;
        const quote = quotes.find(q => q.id === quoteId);
        const maxPrice = quote?.totalAmount || 0;
        const clampedPrice = Math.min(Math.max(0, price), maxPrice);
        return {
          ...prev,
          [quoteId]: {
            ...current,
            price: clampedPrice,
            balance: clampedPrice - current.deposit,
          },
        };
      } else if (field === 'deposit') {
        const deposit = value as number;
        return {
          ...prev,
          [quoteId]: {
            ...current,
            deposit,
            balance: current.price - deposit,
          },
        };
      }
      return prev;
    });
  };

  const handleSaveQuotePaid = async (quoteId: number) => {
    setQuotePaidLoading((prev) => ({ ...prev, [quoteId]: true }));
    try {
      const formData = quotePaidFormData[quoteId];
      if (!formData) {
        throw new Error('Form data not found');
      }

      const response = await apiRequest(`/jobs/${jobId}/quotes/${quoteId}/paid`, {
        method: 'POST',
        body: JSON.stringify({
          price: formData.price,
          deposit: formData.deposit,
          paid: formData.paid,
        }),
      });
      
      const data = response.data as any;
      const paidRecord: Paid = {
        id: data.id,
        price: Number(data.price),
        deposit: Number(data.deposit),
        balance: Number(data.balance),
        paid: data.paid || false,
        quoteId: data.quote_id,
        jobId: data.job_id,
        userId: data.user_id,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
      
      await setQuotePaidRecords((prev) => ({ ...prev, [quoteId]: paidRecord }));
      await setQuoteDeposits((prev) => ({ ...prev, [quoteId]: paidRecord.deposit }));
      
      // Reload job to get updated pay_status
      await loadJob();
      
      setSnackbar({
        open: true,
        message: quotePaidRecords[quoteId] ? 'Paid record updated successfully' : 'Paid record saved successfully',
        severity: 'success',
      });
    } catch (error: any) {
      const errorData = error.response?.data;
      console.error('Error saving paid record for quote:', errorData);
      const errorCode = errorData?.error;
      const statusCode = error.response?.status;

      if (errorCode === 'QUICKBOOKS_NOT_CONNECTED' ||
          (statusCode === 400 && errorData?.message?.includes('QuickBooks'))) {
        const message = errorData?.message || 'QuickBooks Online is not connected. Please connect QuickBooks before updating payment information.';
        setQuickbooksAlertMessage(message);
        setQuickbooksAlertOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: errorData?.message || 'Failed to save paid record',
          severity: 'error',
        });
      }
    } finally {
      setQuotePaidLoading((prev) => ({ ...prev, [quoteId]: false }));
    }
  };

  if (!job && !asTab) {
    return (
      <Box>
        {onBack && (
          <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
            Back to Jobs
          </Button>
        )}
        <Typography variant="h6" color="error">
          Job not found
        </Typography>
      </Box>
    );
  }

  if (asTab && jobLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!job && asTab && !jobLoading) {
    return (
      <Box>
        <Typography variant="h6" color="error">
          Job not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {!asTab && (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            {onBack && (
              <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
                Back to Jobs
              </Button>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="overline" color="text.secondary">
              Quote Management
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                {job?.job}
              </Typography>
              {job && <Chip label={job.status} color="primary" size="medium" />}
            </Box>
          </Box>
        </>
      )}

      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6">Quote List</Typography>
            {!isFieldWorker && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateQuote}
                sx={{ bgcolor: 'grey.900', '&:hover': { bgcolor: 'grey.800' } }}
              >
                Create Quote
              </Button>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : quotes.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography color="text.secondary">No quotes found</Typography>
            </Box>
          ) : (
            <Box>
              {quotes.map((quote) => (
                <Accordion
                  key={quote.id}
                  expanded={expandedQuotes.has(quote.id)}
                  onChange={() => {
                    const newExpanded = new Set(expandedQuotes);
                    if (newExpanded.has(quote.id)) {
                      newExpanded.delete(quote.id);
                    } else {
                      newExpanded.add(quote.id);
                    }
                    setExpandedQuotes(newExpanded);
                  }}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {quote.name}
                          </Typography>
                          {quote.pdfUrl && (
                            <Tooltip title="View PDF">
                              <IconButton
                                component="div"
                                size="small"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    // Extract the path from the full URL and remove /api prefix if present
                                    // since api.get() already has baseURL: '/api'
                                    let path = quote.pdfUrl!;
                                    if (path.startsWith('/api/')) {
                                      path = path.substring(4); // Remove '/api'
                                    } else if (path.startsWith('http')) {
                                      const urlObj = new URL(path);
                                      path = urlObj.pathname + urlObj.search;
                                      if (path.startsWith('/api/')) {
                                        path = path.substring(4); // Remove '/api'
                                      }
                                    }
                                    const response = await api.get(path, {
                                      responseType: 'blob',
                                    });
                                    const blob = new Blob([response.data], { type: 'application/pdf' });
                                    const url = window.URL.createObjectURL(blob);
                                    window.open(url, '_blank');
                                    // Clean up after a delay
                                    setTimeout(() => window.URL.revokeObjectURL(url), 100);
                                  } catch (error) {
                                    console.error('Error opening PDF:', error);
                                    setSnackbar({
                                      open: true,
                                      message: 'Failed to open PDF',
                                      severity: 'error',
                                    });
                                  }
                                }}
                                sx={{ color: 'error.main' }}
                              >
                                <PictureAsPdfIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={quote.status || 'Draft'}
                            onChange={(e) => {
                              e.stopPropagation();
                              const newStatus = e.target.value as string;
                              const currentStatus = quote.status || 'Draft';
                              if (currentStatus === 'Draft' && newStatus === 'Sent') {
                                setPendingStatusChange({ quoteId: quote.id, quote });
                                setWhatsappConfirmOpen(true);
                              } else {
                                handleQuoteStatusChange(quote.id, newStatus, currentStatus);
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            disabled={isFieldWorker}
                            sx={{ height: 32 }}
                          >
                            <MenuItem value="Draft">Draft</MenuItem>
                            <MenuItem value="Sent">Sent</MenuItem>
                            <MenuItem value="Approved">Approved</MenuItem>
                            <MenuItem value="Rejected">Rejected</MenuItem>
                          </Select>
                        </FormControl>
                        <Typography variant="body2" color="text.secondary">
                          {quote.items.length} item{quote.items.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Total: ${(quote.totalAmount || 0).toFixed(2)}
                        </Typography>
                        {!isFieldWorker && (
                          <IconButton
                            component="div"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadTemplate(quote.id);
                            }}
                            title="Download Template"
                            color="primary"
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableChartIcon fontSize="small" />
                          </IconButton>
                        )}
                        {quote.pdfUrl && (
                          <Tooltip title="Download PDF">
                            <IconButton
                              component="div"
                              size="small"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  // Extract the path from the full URL and remove /api prefix if present
                                  // since api.get() already has baseURL: '/api'
                                  let path = quote.pdfUrl!;
                                  if (path.startsWith('/api/')) {
                                    path = path.substring(4); // Remove '/api'
                                  } else if (path.startsWith('http')) {
                                    const urlObj = new URL(path);
                                    path = urlObj.pathname + urlObj.search;
                                    if (path.startsWith('/api/')) {
                                      path = path.substring(4); // Remove '/api'
                                    }
                                  }
                                  const response = await api.get(path, {
                                    responseType: 'blob',
                                  });
                                  const blob = new Blob([response.data], { type: 'application/pdf' });
                                  const downloadUrl = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = downloadUrl;
                                  link.download = `quote_${quote.id}.pdf`;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  window.URL.revokeObjectURL(downloadUrl);
                                } catch (error) {
                                  console.error('Error downloading PDF:', error);
                                  setSnackbar({
                                    open: true,
                                    message: 'Failed to download PDF',
                                    severity: 'error',
                                  });
                                }
                              }}
                              color="error"
                              sx={{ cursor: 'pointer' }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!isFieldWorker && (
                          <IconButton
                            component="div"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditQuote(quote);
                            }}
                            title="Edit Quote"
                            color="primary"
                            sx={{ cursor: 'pointer' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        {isAdmin && (
                          <IconButton
                            component="div"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteQuote(quote.id);
                            }}
                            title="Delete Quote"
                            color="error"
                            sx={{ cursor: 'pointer' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">Quote Items</Typography>
                        {!isFieldWorker && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => handleCreateQuoteItem(quote.id)}
                          >
                            Add Item
                          </Button>
                        )}
                      </Box>
                      {quote.items.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                          No items in this quote. Click "Add Item" to add one.
                        </Typography>
                      ) : (
                        isMobile ? (
                          /* ── Mobile / Tablet: Card layout ── */
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {quote.items.map((item) => {
                              const squareFoot = calculateSquareFoot(item.widthInches, item.heightInches);
                              const linearFoot = calculateLinearFoot(item.widthInches);
                              const rate = calculateRate(
                                squareFoot,
                                item.sqFtRate,
                                linearFoot,
                                item.lnFtRate,
                                item.hardware,
                                item.otherCharges || 0
                              );
                              const productType = productTypes.find(pt => pt.id === item.productId);
                              const hardwareType = productTypes.find(pt => pt.id === item.hardwareId);
                              const productTypeName = productType
                                ? `${productType.productType} (${productType.unit})`
                                : '-';
                              const hardwareTypeName = hardwareType
                                ? `${hardwareType.productType} (${hardwareType.unit})`
                                : '-';

                              return (
                                <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="subtitle2" noWrap>{item.description}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {productTypeName}
                                        {hardwareTypeName !== '-' && ` · ${hardwareTypeName}`}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                                      {!isFieldWorker && (
                                        <IconButton size="small" onClick={() => handleEditQuoteItem(item)} color="primary">
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                      {isAdmin && (
                                        <IconButton size="small" onClick={() => handleDeleteQuoteItem(quote.id, item.id)} color="error">
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                    </Box>
                                  </Box>
                                  <Grid container spacing={1}>
                                    <Grid size={4}>
                                      <Typography variant="caption" color="text.secondary">Size</Typography>
                                      <Typography variant="body2">{item.widthInches}{'" \u00D7 "'}{item.heightInches}"</Typography>
                                    </Grid>
                                    <Grid size={4}>
                                      <Typography variant="caption" color="text.secondary">Sq Ft / Rate</Typography>
                                      <Typography variant="body2">{squareFoot.toFixed(2)} / ${item.sqFtRate.toFixed(2)}</Typography>
                                    </Grid>
                                    <Grid size={4}>
                                      <Typography variant="caption" color="text.secondary">Ln Ft / Rate</Typography>
                                      <Typography variant="body2">{linearFoot.toFixed(2)} / ${item.lnFtRate.toFixed(2)}</Typography>
                                    </Grid>
                                    <Grid size={4}>
                                      <Typography variant="caption" color="text.secondary">Hardware</Typography>
                                      <Typography variant="body2">${item.hardware.toFixed(2)}</Typography>
                                    </Grid>
                                    {(item.otherCharges || 0) > 0 && (
                                      <Grid size={8}>
                                        <Typography variant="caption" color="text.secondary">Other Charges</Typography>
                                        <Typography variant="body2">
                                          ${(item.otherCharges || 0).toFixed(2)}
                                          {item.otherChargesDescription ? ` — ${item.otherChargesDescription}` : ''}
                                        </Typography>
                                      </Grid>
                                    )}
                                    <Grid size={4}>
                                      <Typography variant="caption" color="text.secondary">Qty</Typography>
                                      <Typography variant="body2">{item.quantity}</Typography>
                                    </Grid>
                                    <Grid size={4}>
                                      <Typography variant="caption" color="text.secondary">Rate</Typography>
                                      <Typography variant="body2">${rate.toFixed(2)}</Typography>
                                    </Grid>
                                    <Grid size={4}>
                                      <Typography variant="caption" color="text.secondary">Discount</Typography>
                                      <Typography variant="body2">{(item.discount || 0).toFixed(2)}%</Typography>
                                    </Grid>
                                  </Grid>
                                  <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider', textAlign: 'right' }}>
                                    <Typography variant="subtitle2">
                                      Line Total: ${(item.lineTotal || 0).toFixed(2)}
                                    </Typography>
                                  </Box>
                                </Paper>
                              );
                            })}
                          </Box>
                        ) : (
                          /* ── Desktop: Scrollable compact table ── */
                          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                            <Table size="small" sx={{ minWidth: 1100 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Product Type</TableCell>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Hardware Type</TableCell>
                                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{'W \u00D7 H (in)'}</TableCell>
                                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Sq Ft</TableCell>
                                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>SQ FT Rate</TableCell>
                                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Ln Ft</TableCell>
                                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>LN FT Rate</TableCell>
                                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Hardware</TableCell>
                                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Other Charges</TableCell>
                                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Qty</TableCell>
                                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Rate</TableCell>
                                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Disc %</TableCell>
                                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Line Total</TableCell>
                                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {quote.items.map((item) => {
                                  const squareFoot = calculateSquareFoot(item.widthInches, item.heightInches);
                                  const linearFoot = calculateLinearFoot(item.widthInches);
                                  const rate = calculateRate(
                                    squareFoot,
                                    item.sqFtRate,
                                    linearFoot,
                                    item.lnFtRate,
                                    item.hardware,
                                    item.otherCharges || 0
                                  );
                                  const productType = productTypes.find(pt => pt.id === item.productId);
                                  const hardwareType = productTypes.find(pt => pt.id === item.hardwareId);
                                  const productTypeName = productType
                                    ? `${productType.productType} (${productType.unit})`
                                    : '-';
                                  const hardwareTypeName = hardwareType
                                    ? `${hardwareType.productType} (${hardwareType.unit})`
                                    : '-';

                                  return (
                                    <TableRow key={item.id} hover>
                                      <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        <Tooltip title={productTypeName}><span>{productTypeName}</span></Tooltip>
                                      </TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        <Tooltip title={hardwareTypeName}><span>{hardwareTypeName}</span></Tooltip>
                                      </TableCell>
                                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{item.widthInches}{' \u00D7 '}{item.heightInches}</TableCell>
                                      <TableCell align="right">{squareFoot.toFixed(2)}</TableCell>
                                      <TableCell align="right">${item.sqFtRate.toFixed(2)}</TableCell>
                                      <TableCell align="right">{linearFoot.toFixed(2)}</TableCell>
                                      <TableCell align="right">${item.lnFtRate.toFixed(2)}</TableCell>
                                      <TableCell align="right">${item.hardware.toFixed(2)}</TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 160 }}>
                                        {(item.otherCharges || 0) > 0 ? (
                                          <Tooltip title={item.otherChargesDescription || ''}>
                                            <span>
                                              ${(item.otherCharges || 0).toFixed(2)}
                                              {item.otherChargesDescription ? ` — ${item.otherChargesDescription}` : ''}
                                            </span>
                                          </Tooltip>
                                        ) : '-'}
                                      </TableCell>
                                      <TableCell align="right">{item.quantity}</TableCell>
                                      <TableCell align="right">${rate.toFixed(2)}</TableCell>
                                      <TableCell align="right">{(item.discount || 0).toFixed(2)}%</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 600 }}>${(item.lineTotal || 0).toFixed(2)}</TableCell>
                                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                        {!isFieldWorker && (
                                          <IconButton size="small" onClick={() => handleEditQuoteItem(item)} title="Edit" color="primary">
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        )}
                                        {isAdmin && (
                                          <IconButton size="small" onClick={() => handleDeleteQuoteItem(quote.id, item.id)} title="Delete" color="error">
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )
                      )}
                      {/* Price, Deposit, Balance fields - Show when quote status is Approved */}
                      {quote.status === 'Approved' && (
                        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                          <Typography variant="subtitle2" sx={{ mb: 2 }}>
                            Payment Information
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                              <TextField
                                fullWidth
                                label="Price"
                                type="number"
                                value={quotePaidFormData[quote.id]?.price ?? quote.totalAmount ?? 0}
                                onChange={(e) => handleQuotePaidFormChange(quote.id, 'price', parseFloat(e.target.value) || 0)}
                                disabled={isFieldWorker}
                                inputProps={{
                                  max: quote.totalAmount || 0,
                                  min: 0,
                                }}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                              />
                            </Box>
                            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                              <TextField
                                fullWidth
                                label="Deposit"
                                type="number"
                                value={quotePaidFormData[quote.id]?.deposit ?? quoteDeposits[quote.id] ?? 0}
                                onChange={(e) => handleQuotePaidFormChange(quote.id, 'deposit', parseFloat(e.target.value) || 0)}
                                disabled={isFieldWorker}
                                inputProps={{
                                  min: 0,
                                  max: quotePaidFormData[quote.id]?.price ?? quote.totalAmount ?? 0,
                                }}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                              />
                            </Box>
                            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                              <TextField
                                fullWidth
                                label="Balance"
                                type="number"
                                value={quotePaidFormData[quote.id]?.balance ?? ((quote.totalAmount || 0) - (quoteDeposits[quote.id] || 0))}
                                disabled
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                              />
                            </Box>
                            <Box sx={{ flex: '1 1 200px', minWidth: '200px', display: 'flex', alignItems: 'center' }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={quotePaidFormData[quote.id]?.paid ?? false}
                                    onChange={(e) => handleQuotePaidFormChange(quote.id, 'paid', e.target.checked)}
                                    disabled={isFieldWorker}
                                  />
                                }
                                label="Paid"
                              />
                            </Box>
                          </Box>
                          {!isFieldWorker && (
                            <Box>
                              <Button
                                variant="contained"
                                onClick={() => handleSaveQuotePaid(quote.id)}
                                disabled={quotePaidLoading[quote.id]}
                                sx={{ bgcolor: 'grey.900', '&:hover': { bgcolor: 'grey.800' } }}
                              >
                                {quotePaidLoading[quote.id] ? 'Saving...' : quotePaidRecords[quote.id] ? 'Update' : 'Save'}
                              </Button>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
          {quotes.length > 0 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              {(() => {
                // Calculate total amount with discounts applied
                const totalAmount = calculateTotalAmountWithDiscounts(quotes);

                return (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        Total Amount:
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="bold">
                        ${totalAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  </>
                );
              })()}
            </Box>
          )}

          {/* Payment Section - Show when quote status is Accept */}
          {quoteStatus === 'Accept' && (
            <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Payment Information
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label="Price"
                    type="number"
                    value={paidFormData.price}
                    onChange={(e) => handlePaidFormChange('price', parseFloat(e.target.value) || 0)}
                    disabled={isFieldWorker}
                    inputProps={{
                      max: calculateTotalAmountWithDiscounts(quotes),
                      min: 0,
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label="Deposit"
                    type="number"
                    value={paidFormData.deposit}
                    onChange={(e) => handlePaidFormChange('deposit', parseFloat(e.target.value) || 0)}
                    disabled={isFieldWorker}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    label="Balance"
                    type="number"
                    value={paidFormData.balance}
                    disabled
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: '200px', display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={paidFormData.paid}
                        onChange={(e) => handlePaidFormChange('paid', e.target.checked)}
                        disabled={isFieldWorker}
                      />
                    }
                    label="Paid"
                  />
                </Box>
              </Box>
              {!isFieldWorker && (
                <Box>
                  <Button
                    variant="contained"
                    onClick={handleSavePaid}
                    disabled={paidLoading}
                    sx={{ bgcolor: 'grey.900', '&:hover': { bgcolor: 'grey.800' } }}
                  >
                    {paidLoading ? 'Saving...' : paidRecord ? 'Update' : 'Save'}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Quote Dialog */}
      <Dialog
        open={quoteDialogOpen}
        onClose={() => setQuoteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingQuote ? 'Edit Quote' : 'Create New Quote'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Quote Name"
              value={quoteFormData.name}
              onChange={(e) =>
                setQuoteFormData({ ...quoteFormData, name: e.target.value })
              }
              fullWidth
              required
              placeholder="e.g., Interior Railing"
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={quoteFormData.status}
                label="Status"
                onChange={(e) =>
                  setQuoteFormData({ ...quoteFormData, status: e.target.value })
                }
              >
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="Sent">Sent</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuoteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveQuote}
            variant="contained"
            disabled={!quoteFormData.name}
          >
            {editingQuote ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quote Item Dialog */}
      <Dialog
        open={quoteItemDialogOpen}
        onClose={() => setQuoteItemDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingQuoteItem ? 'Edit Quote Item' : 'Add Quote Item'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Description"
              value={quoteItemFormData.description}
              onChange={(e) =>
                setQuoteItemFormData({
                  ...quoteItemFormData,
                  description: e.target.value,
                })
              }
              fullWidth
              required
              multiline
              minRows={2}
            />
            <Autocomplete
              options={productTypes.filter((pt) => pt.unit === 'Sq ft' || pt.unit === 'Ln ft')}
              getOptionLabel={(option) => {
                if (option.id === -1) {
                  return `Create "${option.productType}"`;
                }
                const unitText = option.unit ? ` (unit: ${option.unit})` : '';
                return `${option.productType || ''}${unitText}`;
              }}
              value={
                productTypes.find((pt) => pt.id === quoteItemFormData.productTypeId) || null
              }
              onChange={(_, newValue) => {
                if (newValue && newValue.id === -1) {
                  setCreateProductTypeForm((prev) => ({
                    ...prev,
                    productType: newValue.productType,
                  }));
                  setCreateProductTypeDialogOpen(true);
                  return;
                }
                
                if (newValue && newValue.id) {
                  let sqFtRate = 0;
                  let lnFtRate = 0;

                  if (newValue.sellingPrice != null) {
                    if (newValue.unit === 'Sq ft') {
                      sqFtRate = newValue.sellingPrice;
                    } else if (newValue.unit === 'Ln ft') {
                      lnFtRate = newValue.sellingPrice;
                    }
                  }

                  setQuoteItemFormData((prev) => ({
                    ...prev,
                    productTypeId: newValue.id,
                    sqFtRate,
                    lnFtRate,
                  }));
                } else {
                  setQuoteItemFormData((prev) => ({
                    ...prev,
                    productTypeId: null,
                    sqFtRate: 0,
                    lnFtRate: 0,
                  }));
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Product Type" fullWidth />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="No options found"
              filterOptions={(options, state) => {
                const filtered = options.filter((option) =>
                  option.productType.toLowerCase().includes(state.inputValue.toLowerCase())
                );
                
                if (state.inputValue && state.inputValue.trim() !== '' && filtered.length === 0) {
                  filtered.push({
                    id: -1,
                    productType: state.inputValue,
                    unit: null,
                    sellingPrice: null,
                  } as QuoteProductType);
                }
                
                return filtered;
              }}
              renderOption={(props, option) => {
                if (option.id === -1) {
                  return (
                    <li {...props} key="create-option" style={{ color: '#1976d2', fontWeight: 500 }}>
                      <AddIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                      Create "{option.productType}"
                    </li>
                  );
                }
                const unitText = option.unit ? ` (unit: ${option.unit})` : '';
                return (
                  <li {...props} key={option.id}>
                    {`${option.productType}${unitText}`}
                  </li>
                );
              }}
            />
            {productTypes.filter((pt) => pt.unit === 'Sq ft' || pt.unit === 'Ln ft').length === 0 && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setCreateProductTypeDialogOpen(true)}
                fullWidth
                sx={{ mt: -1 }}
              >
                Create Product Type
              </Button>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <TextField
                label="Width (inches)"
                type="number"
                value={quoteItemFormData.widthInches}
                onChange={(e) =>
                  setQuoteItemFormData({
                    ...quoteItemFormData,
                    widthInches: parseFloat(e.target.value) || 0,
                  })
                }
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setCalculatorField('width');
                          setCalculatorOpen(true);
                        }}
                        edge="end"
                        size="small"
                        title="Open Calculator"
                      >
                        <CalculateIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Height (inches)"
                type="number"
                value={quoteItemFormData.heightInches}
                onChange={(e) =>
                  setQuoteItemFormData({
                    ...quoteItemFormData,
                    heightInches: parseFloat(e.target.value) || 0,
                  })
                }
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setCalculatorField('height');
                          setCalculatorOpen(true);
                        }}
                        edge="end"
                        size="small"
                        title="Open Calculator"
                      >
                        <CalculateIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Square Foot"
                value={(
                  (quoteItemFormData.widthInches * quoteItemFormData.heightInches) /
                  144
                ).toFixed(2)}
                disabled
                fullWidth
              />
              <TextField
                label="SQ FT Rate ( $ / sq ft )"
                type="number"
                value={quoteItemFormData.sqFtRate}
                disabled
                fullWidth
              />
              <TextField
                label="Linear Foot"
                value={(quoteItemFormData.widthInches / 12).toFixed(2)}
                disabled
                fullWidth
              />
              <TextField
                label="LN FT Rate ( $ / ln ft )"
                type="number"
                value={quoteItemFormData.lnFtRate}
                disabled
                fullWidth
              />
              <Autocomplete
                options={productTypes.filter((pt) => pt.unit === 'Hardware' || pt.unit === 'item')}
                getOptionLabel={(option) => {
                  if (option.id === -1) {
                    return `Create "${option.productType}"`;
                  }
                  return `${option.productType}${option.unit ? ` (unit: ${option.unit})` : ''}`;
                }}
                value={
                  productTypes.find((pt) => pt.id === quoteItemFormData.hardwareProductTypeId) || null
                }
                onChange={(_, newValue) => {
                  if (newValue && newValue.id === -1) {
                    setCreateHardwareTypeForm((prev) => ({
                      ...prev,
                      productType: newValue.productType,
                    }));
                    setCreateHardwareTypeDialogOpen(true);
                    return;
                  }
                  
                  if (newValue && newValue.id) {
                    const hardwareValue =
                      newValue.sellingPrice != null ? newValue.sellingPrice : 0;

                    setQuoteItemFormData((prev) => ({
                      ...prev,
                      hardwareProductTypeId: newValue.id,
                      hardware: hardwareValue,
                    }));
                  } else {
                    setQuoteItemFormData((prev) => ({
                      ...prev,
                      hardwareProductTypeId: null,
                      hardware: 0,
                    }));
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Hardware Type" fullWidth />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No options found"
                filterOptions={(options, state) => {
                  const filtered = options.filter((option) =>
                    option.productType.toLowerCase().includes(state.inputValue.toLowerCase())
                  );
                  
                  if (state.inputValue && state.inputValue.trim() !== '' && filtered.length === 0) {
                    filtered.push({
                      id: -1,
                      productType: state.inputValue,
                      unit: null,
                      sellingPrice: null,
                    } as QuoteProductType);
                  }
                  
                  return filtered;
                }}
                renderOption={(props, option) => {
                  if (option.id === -1) {
                    return (
                      <li {...props} key="create-option" style={{ color: '#1976d2', fontWeight: 500 }}>
                        <AddIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                        Create "{option.productType}"
                      </li>
                    );
                  }
                  return (
                    <li {...props} key={option.id}>
                      {`${option.productType}${option.unit ? ` (unit: ${option.unit})` : ''}`}
                    </li>
                  );
                }}
              />
              {productTypes.filter((pt) => pt.unit === 'Hardware' || pt.unit === 'item').length === 0 && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateHardwareTypeDialogOpen(true)}
                  fullWidth
                  sx={{ mt: -1 }}
                >
                  Create Hardware Type
                </Button>
              )}
              <TextField
                label="Hardware ( $ / hardware )"
                type="number"
                value={quoteItemFormData.hardware}
                disabled
                fullWidth
              />
              <TextField
                label="Description Other Charges (if applicable)"
                value={quoteItemFormData.otherChargesDescription}
                onChange={(e) =>
                  setQuoteItemFormData({
                    ...quoteItemFormData,
                    otherChargesDescription: e.target.value,
                  })
                }
                fullWidth
              />
              <TextField
                label="Other Charges ($/optional)"
                type="number"
                value={quoteItemFormData.otherCharges}
                onChange={(e) =>
                  setQuoteItemFormData({
                    ...quoteItemFormData,
                    otherCharges: parseFloat(e.target.value) || 0,
                  })
                }
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <TextField
                label="Quantity"
                type="number"
                value={quoteItemFormData.quantity}
                onChange={(e) =>
                  setQuoteItemFormData({
                    ...quoteItemFormData,
                    quantity: parseFloat(e.target.value) || 0,
                  })
                }
                fullWidth
              />
              <TextField
                label="Rate"
                value={(
                  (quoteItemFormData.widthInches * quoteItemFormData.heightInches) /
                  144 *
                  quoteItemFormData.sqFtRate +
                  (quoteItemFormData.widthInches / 12) * quoteItemFormData.lnFtRate +
                  quoteItemFormData.hardware +
                  quoteItemFormData.otherCharges
                ).toFixed(2)}
                disabled
                fullWidth
              />
              <TextField
                label="Discount %"
                type="number"
                value={quoteItemFormData.discount}
                onChange={(e) =>
                  setQuoteItemFormData({
                    ...quoteItemFormData,
                    discount: parseFloat(e.target.value) || 0,
                  })
                }
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
              <TextField
                label="Line Total"
                value={(() => {
                  const baseAmount =
                    ((quoteItemFormData.widthInches * quoteItemFormData.heightInches) /
                      144 *
                      quoteItemFormData.sqFtRate +
                      (quoteItemFormData.widthInches / 12) * quoteItemFormData.lnFtRate +
                      quoteItemFormData.hardware +
                      quoteItemFormData.otherCharges) *
                    quoteItemFormData.quantity;
                  const discountMultiplier = 1 - (quoteItemFormData.discount || 0) / 100;
                  return (baseAmount * discountMultiplier).toFixed(2);
                })()}
                disabled
                fullWidth
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuoteItemDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveQuoteItem}
            variant="contained"
            disabled={!quoteItemFormData.description || quoteItemFormData.description.trim() === ''}
          >
            {editingQuoteItem ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Product Type Dialog */}
      <Dialog
        open={createProductTypeDialogOpen}
        onClose={() => setCreateProductTypeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>New Product Type</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Product Type"
              value={createProductTypeForm.productType}
              onChange={(e) =>
                setCreateProductTypeForm({
                  ...createProductTypeForm,
                  productType: e.target.value,
                })
              }
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={createProductTypeForm.description}
              onChange={(e) =>
                setCreateProductTypeForm({
                  ...createProductTypeForm,
                  description: e.target.value,
                })
              }
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Selling Price"
              value={createProductTypeForm.sellingPrice}
              onChange={(e) =>
                setCreateProductTypeForm({
                  ...createProductTypeForm,
                  sellingPrice: e.target.value,
                })
              }
              fullWidth
              required
              type="number"
              inputProps={{ step: '0.01' }}
            />
            <TextField
              select
              label="Unit"
              value={createProductTypeForm.unit}
              onChange={(e) =>
                setCreateProductTypeForm({
                  ...createProductTypeForm,
                  unit: e.target.value,
                })
              }
              fullWidth
            >
              <MenuItem value="Sq ft">Sq ft</MenuItem>
              <MenuItem value="Ln ft">Ln ft</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateProductTypeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateProductType}
            variant="contained"
            disabled={
              createProductTypeSaving ||
              !createProductTypeForm.productType ||
              !createProductTypeForm.sellingPrice
            }
          >
            {createProductTypeSaving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Hardware Type Dialog */}
      <Dialog
        open={createHardwareTypeDialogOpen}
        onClose={() => setCreateHardwareTypeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>New Hardware Type</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Product Type"
              value={createHardwareTypeForm.productType}
              onChange={(e) =>
                setCreateHardwareTypeForm({
                  ...createHardwareTypeForm,
                  productType: e.target.value,
                })
              }
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={createHardwareTypeForm.description}
              onChange={(e) =>
                setCreateHardwareTypeForm({
                  ...createHardwareTypeForm,
                  description: e.target.value,
                })
              }
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Selling Price"
              value={createHardwareTypeForm.sellingPrice}
              onChange={(e) =>
                setCreateHardwareTypeForm({
                  ...createHardwareTypeForm,
                  sellingPrice: e.target.value,
                })
              }
              fullWidth
              required
              type="number"
              inputProps={{ step: '0.01' }}
            />
            <TextField
              select
              label="Unit"
              value={createHardwareTypeForm.unit}
              onChange={(e) =>
                setCreateHardwareTypeForm({
                  ...createHardwareTypeForm,
                  unit: e.target.value,
                })
              }
              fullWidth
            >
              <MenuItem value="Hardware">Hardware</MenuItem>
              <MenuItem value="item">item</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateHardwareTypeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateHardwareType}
            variant="contained"
            disabled={
              createHardwareTypeSaving ||
              !createHardwareTypeForm.productType ||
              !createHardwareTypeForm.sellingPrice
            }
          >
            {createHardwareTypeSaving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Excel Editor Dialog */}
      <Dialog
        open={excelEditorOpen}
        onClose={() => {
          setExcelEditorOpen(false);
          setExcelWorkbook(null);
          setCurrentQuoteIdForExcel(null);
        }}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle>
          Edit Quote Excel Template
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Edit the Excel file and click Download to generate PDF
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, overflow: 'hidden', height: '100%' }}>
          {excelWorkbook && (
            <Box sx={{ height: '100%', overflow: 'auto' }}>
              <ExcelEditor
                onSave={handleExcelEditorSave}
                maxFileSize={20}
                initialWorkbook={excelWorkbook}
                initialFileName={`quote_${currentQuoteIdForExcel}_template.xlsx`}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setExcelEditorOpen(false);
              setExcelWorkbook(null);
              setCurrentQuoteIdForExcel(null);
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Download Loading Modal */}
      <Dialog
        open={downloadLoading}
        disableEscapeKeyDown
        PaperProps={{
          style: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent sx={{ textAlign: 'center', p: 4 }}>
          <CircularProgress size={60} sx={{ mb: 2, color: 'white' }}/>
          <Typography variant="h6" sx={{ mt: 2, color: 'white' }}>
            {excelEditorOpen ? 'Loading Excel template...' : 'Generating PDF...'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, color: 'white' }}>
            {excelEditorOpen ? 'Please wait while we load the template' : 'Please wait while we prepare your quote document'}
          </Typography>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Confirm Modal */}
      <Dialog
        open={whatsappConfirmOpen}
        onClose={() => {
          setWhatsappConfirmOpen(false);
          setPendingStatusChange(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Quotation via WhatsApp</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1 }}>
            You are about to mark this quote as <strong>Sent</strong>. Do you want to send the quotation PDF to the customer via WhatsApp?
          </Alert>
          {job?.customerPhone && (
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              Customer phone: <strong>{job.customerPhone}</strong>
            </Typography>
          )}
          {!job?.customerPhone && (
            <Typography variant="body2" sx={{ mt: 2, color: 'warning.main' }}>
              No customer phone number on file. You can still mark the quote as sent.
            </Typography>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Note:</strong> WhatsApp only supports pre-filled text messages via link. The PDF will <strong>not</strong> be attached automatically — you will need to manually attach it in the WhatsApp chat after it opens.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setWhatsappConfirmOpen(false);
              setPendingStatusChange(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (pendingStatusChange) {
                await handleQuoteStatusChange(pendingStatusChange.quoteId, 'Sent');
              }
              setWhatsappConfirmOpen(false);
              setPendingStatusChange(null);
            }}
            variant="outlined"
          >
            Mark as Sent Only
          </Button>
          <Button
            onClick={async () => {
              if (pendingStatusChange) {
                await handleQuoteStatusChange(pendingStatusChange.quoteId, 'Sent');
                const phone = job?.customerPhone?.replace(/\D/g, '') || '';
                const quoteName = pendingStatusChange.quote.name || 'your quote';
                const message = encodeURIComponent(`Hi ${job?.customer || 'there'}, please find attached your quotation "${quoteName}". Let us know if you have any questions!`);
                const url = phone
                  ? `https://wa.me/${phone}?text=${message}`
                  : `https://wa.me/?text=${message}`;
                window.open(url, '_blank');
              }
              setWhatsappConfirmOpen(false);
              setPendingStatusChange(null);
            }}
            variant="contained"
            color="success"
            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1ebe57' } }}
          >
            Send via WhatsApp
          </Button>
        </DialogActions>
      </Dialog>

      {/* Job Status Alert Modal */}
      <Dialog
        open={jobStatusAlertOpen}
        onClose={() => {
          setJobStatusAlertOpen(false);
          setNewJobStatus(null);
          setJobActiveUpdated(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {jobActiveUpdated && !newJobStatus ? 'Job Set to Inactive' : 'Job Status Updated'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {newJobStatus && (
              <Alert severity="success">
                {newJobStatus === 'Proposal' && (
                  <>All quotes for this job have been sent. The job status has been automatically updated to "Proposal".</>
                )}
                {newJobStatus === 'Measure' && (
                  <>All quotes for this job are approved. The job status has been automatically updated to "Measure".</>
                )}
                {newJobStatus && newJobStatus !== 'Proposal' && newJobStatus !== 'Measure' && (
                  <>The job status has been automatically updated to "{newJobStatus}".</>
                )}
              </Alert>
            )}
            {jobActiveUpdated && (
              <Alert severity="warning">
                All quotes for this job have been rejected. The job has been set to inactive.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setJobStatusAlertOpen(false);
              setNewJobStatus(null);
              setJobActiveUpdated(false);
            }} 
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* QuickBooks Connection Alert Dialog */}
      <Dialog
        open={quickbooksAlertOpen}
        onClose={() => {
          setQuickbooksAlertOpen(false);
          // Redirect to integrations page when dialog is closed
          setTimeout(() => {
            window.location.href = '/integrations';
          }, 500);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          QuickBooks Connection Required
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 2 }}>
            {quickbooksAlertMessage}
          </Alert>
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            You will be redirected to the Integrations page to connect QuickBooks Online.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setQuickbooksAlertOpen(false);
              // Redirect to integrations page
              window.location.href = '/integrations';
            }}
            variant="contained"
            color="primary"
          >
            Go to Integrations
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Calculator Dialog */}
      <Calculator
        open={calculatorOpen}
        onClose={() => setCalculatorOpen(false)}
        onCalculate={(value) => {
          if (calculatorField === 'width') {
            setQuoteItemFormData({
              ...quoteItemFormData,
              widthInches: value,
            });
          } else {
            setQuoteItemFormData({
              ...quoteItemFormData,
              heightInches: value,
            });
          }
        }}
        initialValue={calculatorField === 'width' ? quoteItemFormData.widthInches : quoteItemFormData.heightInches}
      />
    </Box>
  );
}
